'use strict';

import { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Link } from 'react-router';

import UserStore from '@stores/UserStore';

import './MembershipExpiredBanner.scss';

// Copy varies by whether a free trial lapsed or a paid membership lapsed. New
// consumers default to subscription_plan 'free_trial' (users CreateUser.php), so
// anything else means they previously held a paid plan and are now reactivating.
const BANNER_COPY = {
    trial: {
        headline: 'Your free trial has ended',
        subhead: 'Get highly personalized meals planned around your tastes and goals, week after week. Grocery lists included.',
        cta: 'Choose Your Plan',
    },
    subscription: {
        headline: 'Your membership has ended',
        subhead: 'Welcome back. Your meal plans, favorites, and preferences are still saved, so you can pick up right where you left off.',
        cta: 'Reactivate Membership',
    },
};

export default class MembershipExpiredBanner extends Component {
    static propTypes = {
        // The membership page passes showCta={false} (the CTA would link to the page
        // you're already on) and inline (render in page flow, not fixed to the top).
        showCta: PropTypes.bool,
        inline: PropTypes.bool,
    };

    static defaultProps = {
        showCta: true,
        inline: false,
    };

    constructor(props) {
        super(props);

        this.state = {
            user: UserStore.getUser(),
        };

        this.bannerRef = createRef();
    }

    componentDidMount = () => {
        UserStore.addChangeListener(this.onUserStoreChange);

        // Only the fixed (dashboard) variant needs to reserve space below the header.
        if (!this.props.inline) {
            window.addEventListener('resize', this.syncBannerHeight);
            this.syncBannerHeight();
        }
    }

    componentDidUpdate = () => {
        if (!this.props.inline) {
            this.syncBannerHeight();
        }
    }

    componentWillUnmount = () => {
        UserStore.removeChangeListener(this.onUserStoreChange);

        if (!this.props.inline) {
            window.removeEventListener('resize', this.syncBannerHeight);
            // Reset the offset so the meal-feed toolbar/content return to normal.
            document.documentElement.style.setProperty('--membership-banner-height', '0px');
        }
    }

    onUserStoreChange = () => {
        this.setState({user: UserStore.getUser()});
    }

    // The fixed banner is position:fixed, so it can't push siblings. Publish its
    // rendered height (0 when hidden) as a CSS var; the fixed meal-feed toolbar and
    // the feed content padding add it so they sit below the banner.
    syncBannerHeight = () => {
        const height = this.bannerRef.current ? this.bannerRef.current.offsetHeight : 0;
        document.documentElement.style.setProperty('--membership-banner-height', `${height}px`);
    }

    render() {
        const { showCta, inline } = this.props;
        const { user } = this.state;

        if (!user) {
            return <span />;
        }

        // Only self-service consumers can purchase their own membership. Managed
        // (dietitian-owned) consumers and PRO users have this capability disabled.
        if (!(user.capabilities && user.capabilities.purchase_membership)) {
            return <span />;
        }

        if (!user.active_until || !moment(user.active_until).isBefore(moment())) {
            return <span />;
        }

        const copy = user.subscription_plan === 'free_trial' ? BANNER_COPY.trial : BANNER_COPY.subscription;
        const className = 'membership-expired-banner' + (inline ? ' is-inline' : '');

        return (
            <span>
                <div className={className} ref={this.bannerRef}><div className="banner-container">
                    <div className="banner-text">
                        <h3>{copy.headline}</h3>
                        <p>{copy.subhead}</p>
                    </div>

                    {showCta ?
                        <footer>
                            <Link to="/membership"><button>{copy.cta}</button></Link>
                        </footer>
                    : null}
                </div></div>
            </span>
        );
    }
}
